import React, { useState, useEffect } from "react";
import { Store } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const AmmaMarunthagam = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [ammaData, setAmmaData] = useState(null);
  const [medicals, setMedicals] = useState([]);

  const MEDIA_BASE_URL = 'https://kooturavu.tn.gov.in';

  useEffect(() => {
    fetchAmmaData();
    fetchMedicals();
  }, []);

  const fetchAmmaData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/amma_marunthagam/get_content`);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setAmmaData(result.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching Amma Marunthagam data:', error);
      setLoading(false);
    }
  };

  const fetchMedicals = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/amma_marunthagam/medicals/get_all`);
      const result = await response.json();
      
      if (result.status === 'success' && result.data) {
        setMedicals(result.data);
      }
    } catch (error) {
      console.error('Error fetching medicals:', error);
    }
  };

  const content = {
    en: {
      overview: "Overview",
      keyStats: "Key Stats",
      storesLabel: "Amma Marunthagam Stores",
      tableTitle: "List of Amma Marunthagam",
      tableHeaders: {
        sno: "S.No.",
        region: "Region",
        institution: "Institution",
        shopName: "Shop Name"
      }
    },
    ta: {
      overview: "கண்ணோட்டம்",
      keyStats: "முக்கிய புள்ளிவிவரங்கள்",
      storesLabel: "அம்மா மருந்தகம் கடைகள்",
      tableTitle: "அம்மா மருந்தகம் பட்டியல்",
      tableHeaders: {
        sno: "வ.எண்.",
        region: "பிராந்தியம்",
        institution: "நிறுவனம்",
        shopName: "கடை பெயர்"
      }
    }
  };

  const t = content[language];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!ammaData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">No Amma Marunthagam data available</div>
      </div>
    );
  }

  return (
    <section id="amma-section" className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm">

      {/* Overview */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.overview}</h3>
        <div className="bg-purple-50 p-6 rounded-lg">
          <p className="text-gray-700 leading-relaxed">
            {language === 'en' ? ammaData.overview_eng : ammaData.overview_tamil}
          </p>
        </div>
      </div>

      {/* Key Stats */}
      {ammaData.key_stats && ammaData.key_stats.length > 0 ? (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyStats}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ammaData.key_stats.map((stat, index) => (
              <div key={index} className="bg-fuchsia-100 p-6 text-center rounded-lg">
                {stat.logo_path && (
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-white mb-3 rounded-full">
                    <img 
                      src={`${MEDIA_BASE_URL}/${stat.logo_path}`} 
                      alt="stat logo" 
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                )}
                <div className="text-2xl font-bold text-gray-900">
                  {language === 'en' ? stat.heading_eng : stat.heading_tamil}
                </div>
                <div className="text-sm font-semibold text-gray-700 mt-2">
                  {language === 'en' ? stat.content_eng : stat.content_tamil}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.keyStats}</h3>
          <div className="flex justify-center">
            <div className="bg-fuchsia-100 p-6 text-center max-w-sm w-full rounded-lg">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white text-gray-700 mb-3 rounded-full">
                <Store />
              </div>
              <div className="text-2xl font-bold text-gray-900">{medicals.length}</div>
              <div className="text-sm font-semibold text-gray-700 mt-2">{t.storesLabel}</div>
            </div>
          </div>
        </div>
      )}

      {/* List of Amma Marunthagam */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.tableTitle}</h3>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-gray-200">
                <tr>
                  <th className="p-2 sm:p-3 text-left text-gray-800 font-semibold border border-gray-300 whitespace-nowrap">{t.tableHeaders.sno}</th>
                  <th className="p-2 sm:p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.region}</th>
                  <th className="p-2 sm:p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.institution}</th>
                  <th className="p-2 sm:p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.shopName}</th>
                </tr>
              </thead>
              <tbody>
                {medicals.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-3 text-center text-gray-500">
                      No medical shops available
                    </td>
                  </tr>
                ) : (
                  medicals.map((item) => (
                    <tr key={item.id} className="border-b border-gray-300 hover:bg-gray-50">
                      <td className="p-2 sm:p-3 text-gray-800 font-medium border border-gray-300">{item.Sl_No}</td>
                      <td className="p-2 sm:p-3 text-gray-700 border border-gray-300">{item.Name_of_the_Region}</td>
                      <td className="p-2 sm:p-3 text-gray-700 border border-gray-300">{item.Name_of_the_institutions}</td>
                      <td className="p-2 sm:p-3 text-gray-700 border border-gray-300">{item.Name_of_the_Shops}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </section>
  );
};

export default AmmaMarunthagam;